import type { BrandType } from 'noddi-types';
import { useContext } from 'react';
import { createStore, useStore } from 'zustand';
import { createJSONStorage, devtools, persist } from 'zustand/middleware';
import { brandCustomerServiceEmails, brandUrls, onBrandChange } from '../../utils/brand';
import { BrandContext } from './brandContext';
import type { BrandState } from './brandStateType';
import { syncBrandAnalytics } from './syncBrandAnalytics';

interface BrandStoreProps {
  initialBrand: BrandType;
}

// NB, keep stuff in multiple files to avoid HMR issues with zustand stores..

export const createBrandStore = (props: BrandStoreProps) => {
  // Super-prop + OpenPanel group + API header. Re-run on setBrand and after
  // persist rehydrate (BrandAnalyticsTracker). `{ boot: true }` keeps the group
  // across logout.
  syncBrandAnalytics(props.initialBrand);

  return createStore<BrandState>()(
    devtools(
      persist(
        (set) => ({
          currentBrand: props.initialBrand,
          setBrand: (brand) => {
            set({ currentBrand: brand });
            syncBrandAnalytics(brand);
            onBrandChange(brand);
          }
        }),
        {
          name: 'brand-store',
          storage: createJSONStorage(() => sessionStorage),
          partialize: (state) => ({
            currentBrand: state.currentBrand
          })
        }
      )
    )
  );
};

function useBrandContext<T>(selector: (state: BrandState) => T): T {
  const store = useContext(BrandContext);
  if (!store) {
    throw new Error('Missing BrandContext.Provider in the tree');
  }
  return useStore(store, selector);
}

export const useCurrentBrand = () => {
  const currentBrand = useBrandContext((state) => state.currentBrand);
  return {
    currentBrand,
    brandEmail: brandCustomerServiceEmails[currentBrand],
    brandUrl: brandUrls[currentBrand]
  };
};
export const useSetBrand = () => useBrandContext((state) => state.setBrand);
